import { Box, Checkbox, Grid, Typography } from '@mui/material';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { NumberField } from '../../components/form';
import ReadOnlyField from '../../components/form/readonly-field';
import { Dealer, LoadUnitLabels, Order, OrderLoadRatingFormData } from '../../types';
import OrderUpdateDialog, { OrderFormData } from './order-update-dialog';

interface OrderLoadRatingDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  dealer?: Dealer;
  onSubmit: (data: OrderLoadRatingFormData) => Promise<void>;
  isSubmitting?: boolean;
}

const LoadRatingFields: React.FC = () => {
  const { control } = useFormContext();

  const windLoad = useWatch({ control, name: 'windLoad' });
  const snowLoad = useWatch({ control, name: 'snowLoad' });

  return (
    <Grid container spacing={3}>
      {/* Live Load */}
      <Grid item xs={12} md={3}>
        <NumberField
          name="liveLoad"
          control={control}
          label="Live Load"
          isReadOnly={true}
          required={true}
          min={0}
          step={0.1}
          unitLabel={LoadUnitLabels.WEIGHT}
        />
      </Grid>

      {/* Dead Load */}
      <Grid item xs={12} md={3}>
        <NumberField
          name="deadLoad"
          control={control}
          label="Dead Load"
          isReadOnly={true}
          required={true}
          min={0}
          step={0.1}
          unitLabel={LoadUnitLabels.WEIGHT}
        />
      </Grid>

      {/* Wind Load - ASCE read-only */}
      <Grid item xs={12} md={3}>
        <ReadOnlyField label="Wind Load" value={windLoad > 0 ? `${windLoad} mph` : '--'} />
        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
          ASCE Load Rating
        </Typography>
      </Grid>

      {/* Snow Load - ASCE read-only */}
      <Grid item xs={12} md={3}>
        <ReadOnlyField
          label="Snow Load"
          value={snowLoad != null && windLoad > 0 ? `${snowLoad} psf` : '--'}
        />
        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
          ASCE Load Rating
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Wind Load and Snow Load are auto-populated from the ASCE API based on Job Location
          coordinates. To change these values, update the Job Location.
        </Typography>
      </Grid>

      {/* Confirm Load Rating - read-only in modal */}
      <Grid item xs={12}>
        <Controller
          name="confirmedLoadRating"
          control={control}
          render={({ field }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, opacity: 0.7 }}>
              <Checkbox checked={!!field.value} disabled sx={{ mr: 1 }} />
              <Box>
                <Typography>Confirm Load Rating</Typography>
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                  (read-only — re-confirmation is prompted automatically when Job Location changes
                  trigger new ASCE values)
                </Typography>
              </Box>
            </Box>
          )}
        />
      </Grid>
    </Grid>
  );
};

const OrderLoadRatingDialog: React.FC<OrderLoadRatingDialogProps> = ({
  open,
  onClose,
  order,
  onSubmit,
  isSubmitting = false,
}) => {
  const handleSubmit = async (data: OrderFormData) => {
    const cleaned = { ...(data as Record<string, unknown>) };
    delete (cleaned as Record<string, unknown>).stateConfirmed;
    await onSubmit(cleaned as unknown as OrderLoadRatingFormData);
  };

  return (
    <OrderUpdateDialog
      open={open}
      onClose={onClose}
      order={order}
      title="Load Rating"
      schemaKey="loadRating"
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    >
      <LoadRatingFields />
    </OrderUpdateDialog>
  );
};

export default OrderLoadRatingDialog;
